/**
 * useRbac — Frontend RBAC chip helper (Feb 2026)
 *
 * Loads the caller's effective permission set from `/rbac/me/permissions`
 * once at boot (cached via React context) and exposes:
 *   • `can(module, action)` → boolean
 *   • `<PermButton module="…" action="…" …>` — auto-disables + tooltips
 *     with "🔒 <module>.<action>" when the caller lacks the grant.
 *
 * Administrator short-circuits every check to True. The permission set
 * is cached in-memory for the session; a stale mount (post-role-change)
 * can call `refresh()` to re-pull without a full reload.
 *
 * F1 · D-30 · Active-session permission synchronisation (Jun 2026)
 * ────────────────────────────────────────────────────────────────
 * Backend remains authoritative — a 403 on a mutating call is the
 * contract. The UI, however, previously cached permissions for the
 * entire session, so a role-grant change (add OR revoke) by an admin
 * mid-session left stale sidebar/button state until re-login. D-30
 * closes that gap with THREE minimal triggers (NO polling, NO forced
 * logout, NO repeated fetches):
 *
 *   1. `scheduleRefresh()` — module-level debounced refresh with a
 *      3-second cool-down. Caps backend traffic to at most one
 *      `/rbac/me/permissions` call per 3 s even under rapid stimuli.
 *   2. Axios 403 response interceptor — any backend 403 while a
 *      session is active schedules a refresh. If permissions changed
 *      the next render reflects the live state.
 *   3. React Router pathname-change listener — scheduled refresh on
 *      navigation. The 3-second cool-down absorbs rapid route jumps.
 *
 * This design preserves immediate backend enforcement, limits refresh
 * traffic to legitimate stimuli, and never kicks a user to the login
 * screen. See `backend/tests/test_rbac_package_a_d30_sync.py` for the
 * accompanying regression tests (synchronisation + revocation).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, _rbacSetEffective } from "@/lib/auth";

const RbacCtx = createContext({
  ready: false,
  administrator: false,
  can: () => false,
  refresh: () => {},
});

// Module-level handle so the Axios 403 interceptor (installed below)
// can request a refresh without needing React context access.
let _activeRefresh = null;
let _lastRefreshAt = 0;
const _REFRESH_COOLDOWN_MS = 3000;

export function scheduleRbacRefresh() {
  if (!_activeRefresh) return;
  const now = Date.now();
  if (now - _lastRefreshAt < _REFRESH_COOLDOWN_MS) return;
  _lastRefreshAt = now;
  // Fire-and-forget — refresh errors are already swallowed inside load().
  _activeRefresh();
}

// Install the Axios 403 interceptor exactly once. We do it at module
// load so every `api.*` call authored anywhere in the frontend is
// covered — no page-level opt-in required. The interceptor NEVER
// swallows the error; it rethrows so existing toasts / handlers run
// unchanged.
let _interceptorInstalled = false;
function _installAxios403Interceptor() {
  if (_interceptorInstalled) return;
  _interceptorInstalled = true;
  try {
    api.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error && error.response && error.response.status;
        if (status === 403) scheduleRbacRefresh();
        return Promise.reject(error);
      },
    );
  } catch {
    // Defensive — if `api` doesn't expose interceptors in a given test
    // harness, D-30 degrades gracefully (navigation-trigger still runs).
  }
}
_installAxios403Interceptor();

export function RbacProvider({ children }) {
  const { user } = useAuth();
  // REL-02 · state is tagged with the identity it was loaded for; `ready` is only true for the
  // signed-in user, so a login never acts on the previous (or logged-out) permission set.
  const uid = user ? (user.id || user.email || "user") : "";
  const [state, setState] = useState({ ready: false, uid: null, administrator: false, perms: new Set() });
  const location = useLocation();
  const uidRef = useRef(uid);
  uidRef.current = uid;

  const load = useCallback(async () => {
    if (!user) {
      _rbacSetEffective(false, []);
      setState({ ready: true, uid: "", administrator: false, perms: new Set() });
      return;
    }
    try {
      const { data } = await api.get("/rbac/me/permissions");
      const perms = new Set((data.permissions || []).map(p => `${p.module_code}:${p.action}`));
      if (uidRef.current !== uid) return;  // response for a previous identity
      _rbacSetEffective(!!data.administrator, data.permissions || []);
      setState({ ready: true, uid, administrator: !!data.administrator, perms });
    } catch {
      if (uidRef.current !== uid) return;
      _rbacSetEffective(false, []);
      setState({ ready: true, uid, administrator: false, perms: new Set() });
    }
  }, [user, uid]);

  // Expose `load` to the module-level handle so the Axios 403
  // interceptor + navigation listener can schedule a debounced refresh.
  useEffect(() => {
    _activeRefresh = load;
    return () => { if (_activeRefresh === load) _activeRefresh = null; };
  }, [load]);

  // D-30 · Trigger (3) · Pathname-change refresh (debounced).
  useEffect(() => {
    if (!user) return;
    scheduleRbacRefresh();
  }, [location.pathname, user]);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(() => {
    const isAdmin = state.administrator;
    const perms = state.perms;
    const hasPerm = (perm) => {
      if (!perm) return false;
      if (Array.isArray(perm)) return perm.some(p => hasPerm(p));
      if (isAdmin) return true;
      const s = String(perm);
      if (perms.has(s)) return true;
      // Accept legacy `.` separator variants
      const dot = s.replace(":", ".");
      return perms.has(dot);
    };
    return {
      ready: state.ready && state.uid === uid,
      administrator: isAdmin,
      can: (module_code, action) => isAdmin || perms.has(`${module_code}:${action}`),
      // Aug 2026 · Compat shim — accepts a colon-notation `module:action`
      // string so page code that was written against the `auth.jsx`
      // `hasPerm` API works verbatim when it destructures from
      // `useRbac()`.
      hasPerm,
      refresh: load,
    };
  }, [state, load, uid]);

  return <RbacCtx.Provider value={value}>{children}</RbacCtx.Provider>;
}

export function useRbac() { return useContext(RbacCtx); }

/**
 * <PermButton module="sales_orders" action="edit" ...>
 * Renders a `<button>` that auto-disables + shows a 🔒 tooltip when the
 * caller lacks the required grant. Passes every other prop through so
 * class names, click handlers, data-testid etc. work as usual.
 */
export function PermButton({
  module: mod,
  action,
  disabled = false,
  title,
  children,
  onClick,
  className = "",
  ...rest
}) {
  const { can, ready } = useRbac();
  const allowed = can(mod, action);
  const lockedTip = `🔒 ${mod}.${action} — ask an admin to grant this`;
  return (
    <button
      {...rest}
      onClick={allowed ? onClick : undefined}
      disabled={disabled || !allowed || !ready}
      title={allowed ? (title || undefined) : lockedTip}
      aria-disabled={disabled || !allowed}
      data-perm={`${mod}:${action}`}
      className={`${className} ${!allowed ? "opacity-50 cursor-not-allowed" : ""}`.trim()}
    >
      {!allowed && <span aria-hidden="true" className="mr-1 text-[10px]">🔒</span>}
      {children}
    </button>
  );
}
